/* PromptForm.tsx generated by @compiled/babel-plugin v0.36.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./PromptForm.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */

/** @jsxfrag */

import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { getEmptyADF, isEmpty } from '@atlaskit/adf-utils/empty-adf';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { fontFallback } from '@atlaskit/theme/typography';
import { useElementBreakpoints, Breakpoint } from '../../hooks/useElementBreakpoints';
import { AtlassianIntelligenceLogo } from '../AtlassianIntelligenceLogo/AtlassianIntelligenceLogo';
import { BackIconButton } from '../BackIconButton/BackIconButton';
import { PromptEditorWrapper } from '../PromptEditorWrapper/PromptEditorWrapper';
import { SubmitButton } from '../SubmitButton/SubmitButton';
import messages from './messages';
var commonTextStyles = null;
var formGapStyles = null;

// Make form height equals the height of its parent to prevent the form from growing out of the parent
var formHeightStyles = null;

// Prevent form inner from growing out of form
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var formInnerHeightStyles = null;

// input's line-height is 20px, logo/button's height is 24px, need to adjust the height
var nextToInputStyles = null;
var selfStartStyles = null;
var formContainerStyles = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var formStyles = null;
var formPaddingTopStyles = null;
var formInnerStyles = null;
var formInnerPaddingTopStyles = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var buttonWrapperStyles = null;
export var PromptForm = function PromptForm(_ref) {
  var _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'user-input' : _ref$type,
    onFormSubmit = _ref.onFormSubmit,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$showButtons = _ref.showButtons,
    showButtons = _ref$showButtons === void 0 ? true : _ref$showButtons,
    tag = _ref.tag,
    _ref$autoFocus = _ref.autoFocus,
    autoFocus = _ref$autoFocus === void 0 ? true : _ref$autoFocus,
    onInputChange = _ref.onInputChange,
    onADFChange = _ref.onADFChange,
    onInputFocus = _ref.onInputFocus,
    onInputBlur = _ref.onInputBlur,
    placeholder = _ref.placeholder,
    focusInputRef = _ref.focusInputRef,
    clearInputRef = _ref.clearInputRef,
    _ref$showLogo = _ref.showLogo,
    showLogo = _ref$showLogo === void 0 ? true : _ref$showLogo,
    onInputKeyDown = _ref.onInputKeyDown,
    adfValue = _ref.adfValue,
    enableLinks = _ref.enableLinks,
    PromptEditor = _ref.PromptEditor,
    showBack = _ref.showBack,
    onBack = _ref.onBack,
    onInputKeyUp = _ref.onInputKeyUp,
    fireParentActionButton = _ref.fireParentActionButton,
    focusParentActionButton = _ref.focusParentActionButton,
    refinementTagRef = _ref.refinementTagRef;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useElementBreakpoint = useElementBreakpoints(),
    _useElementBreakpoint2 = _slicedToArray(_useElementBreakpoint, 2),
    setBreakpointElement = _useElementBreakpoint2[0],
    breakpoint = _useElementBreakpoint2[1].breakpoint;
  var blurInputRef = useRef(undefined);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isComposing = _useState2[0],
    setIsComposing = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLozengeSelected = _useState4[0],
    setIsLozengeSelected = _useState4[1];
  var lastAdfValue = useRef(getEmptyADF());
  var isPromptInputEmpty = Boolean(isEmpty(lastAdfValue === null || lastAdfValue === void 0 ? void 0 : lastAdfValue.current) && adfValue && isEmpty(adfValue));
  var handleOnCompositionStart = useCallback(function () {
    setIsComposing(true);
  }, []);
  var handleOnCompositionEnd = useCallback(function () {
    setIsComposing(false);
  }, []);
  var handleKeyUp = React.useCallback(function (event) {
    if (isPromptInputEmpty && focusParentActionButton) {
      focusParentActionButton(true);
    }
    onInputKeyUp && onInputKeyUp(event);
  }, [focusParentActionButton, onInputKeyUp, isPromptInputEmpty]);
  var handleInputFocus = React.useCallback(function () {
    setIsLozengeSelected(false);
    if (onInputFocus) {
      onInputFocus();
    }
  }, [onInputFocus]);
  var handleKeyDown = React.useCallback(function (event) {
    /**
     * If the key is printable or escape, we should remove the focus ring
     * that is applied to the parent insert or replace button
     */
    if ((event.key.length === 1 || event.key === 'Escape') && focusParentActionButton) {
      focusParentActionButton(false);
    }
    if (event.key === 'Backspace' && onBack) {
      // This shortcuts the default functionality by placing focus on the refinement tag
      // instead of removing the tag
      if (!isLozengeSelected && isPromptInputEmpty && refinementTagRef !== null && refinementTagRef !== void 0 && refinementTagRef.current) {
        refinementTagRef.current.getElementsByTagName('button')[0].focus();
        setIsLozengeSelected(true);
        return;
      }
      event.stopPropagation();
      if (isPromptInputEmpty) {
        onBack();
      }
    }
    if (event.key === 'Enter' && [event.altKey, event.metaKey, event.ctrlKey].every(function (isPressed) {
      return !isPressed;
    })) {
      var _target$getAttribute;
      event.stopPropagation();
      // eslint-disable-next-line @atlaskit/editor/no-as-casting
      var target = event.target;
      if (isComposing) {
        return;
      }

      // If the prompt input is empty, we should fire the parent action button
      // on Enter, which will either be replace or insert
      if (isPromptInputEmpty && fireParentActionButton) {
        fireParentActionButton();
        return;
      }

      // Handles a case where the user presses enter if refinement tag's close button or the
      // cancel button is focused
      if (['close-button-refinement-tag', 'cancel-icon-button', 'cancel-esc-button', 'back-icon-button'].includes((_target$getAttribute = target.getAttribute('data-testid')) !== null && _target$getAttribute !== void 0 ? _target$getAttribute : '')) {
        return;
      }
      // Handles every other case that is not a "shift + enter"
      if (!event.shiftKey) {
        event.preventDefault();
        onFormSubmit(value);
      }
    }

    // Return focus back to the input if the user is typing
    // and the refinement tag removing button is focused.
    if (!['Enter', 'Backspace'].includes(event.key) && refinementTagRef !== null && refinementTagRef !== void 0 && refinementTagRef.current && refinementTagRef.current.contains(document.activeElement)) {
      var _document$getElementB;
      (_document$getElementB = document.getElementById('ak-editor-textarea')) === null || _document$getElementB === void 0 || _document$getElementB.focus();
    }
  }, [focusParentActionButton, onBack, refinementTagRef, isLozengeSelected, isPromptInputEmpty, isComposing, fireParentActionButton, onFormSubmit, value]);
  var onADFChange_ = React.useCallback(function (input) {
    lastAdfValue.current = adfValue !== null && adfValue !== void 0 ? adfValue : getEmptyADF();
    if (onADFChange) {
      onADFChange(input);
    }
  }, [onADFChange, adfValue]);
  var onInputChange_ = React.useCallback(function (newValue) {
    if (onInputChange) {
      onInputChange(newValue);
    }
  }, [onInputChange]);
  var handleSubmitClick = React.useCallback(function () {
    onFormSubmit(value);
  }, [onFormSubmit, value]);
  return /*#__PURE__*/React.createElement("div", {
    // ED-19496: We avoid propagating enter keypresses
    // since products like Bitbucket might have parent forms
    // that listen for keydown events to trigger form submission
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    ref: setBreakpointElement,
    className: ax(["_16jlkb7n _1o9zkb7n _i0dlf1ug _1e0c1txw _1bah1y6m _4cvr1y6m"])
  }, showLogo && /*#__PURE__*/React.createElement("div", {
    className: ax(["_19pk1i6y _otyr1i6y _1e0c1txw _2lx2vrvc _2hwx1b66 _4cvr1h6o _1wpz1wug", breakpoint <= Breakpoint.Small && "_1wpzynz6"])
  }, /*#__PURE__*/React.createElement(AtlassianIntelligenceIcon, {
    color: "var(--ds-icon, #44546F)",
    label: formatMessage(messages.logo),
    spacing: "spacious",
    LEGACY_fallbackIcon: AtlassianIntelligenceLogo,
    LEGACY_size: "medium"
  })), showBack && /*#__PURE__*/React.createElement(BackIconButton, {
    inPromptHeader: true,
    stopPropagation: true,
    onClick: onBack
  }), /*#__PURE__*/React.createElement("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: ax(["_rcujidpf _o81cidpf _1l6uidpf _149midpf _1vhmkb7n _tqf3kb7n _1rnvf1ug _if291txw _2b921bp4", "_zulp1b66", showLogo && "_ca0qv77o", "_4t3i1osq", "ai-modal-prompt-form"]),
    "data-testid": "ai-modal-prompt-form"
  }, tag, /*#__PURE__*/React.createElement("div", {
    className: ax(["_1nmz1hna _zzcc4f4w _x3fxnil1 _u6rdk9i7 _v2glf7ua", "_16jlkb7n _1o9zkb7n _i0dlf1ug _1e0c1txw", !!tag && "_ca0q1b66", "_1tkeidpf"])
  }, /*#__PURE__*/React.createElement(PromptEditorWrapper, {
    PromptEditor: PromptEditor,
    type: type,
    focusInputRef: focusInputRef,
    blurInputRef: blurInputRef,
    clearInputRef: clearInputRef,
    autoFocus: autoFocus,
    defaultValue: adfValue,
    onInputChange: onInputChange_,
    onADFChange: onADFChange_,
    placeholder: placeholder,
    onFocus: handleInputFocus,
    onBlur: onInputBlur,
    onKeyDown: onInputKeyDown,
    onCompositionStart: handleOnCompositionStart,
    onCompositionEnd: handleOnCompositionEnd,
    enableLinks: enableLinks
  }), showButtons && /*#__PURE__*/React.createElement("div", {
    className: ax(["_1aaxusic _1e0c1txw _18u01b66"])
  }, /*#__PURE__*/React.createElement(SubmitButton, {
    disabled: !value,
    onClick: handleSubmitClick
  })))));
};